import { ChevronDown, MoveHorizontal } from 'lucide-react'

import { cn } from '@/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Slider } from '@/components/ui/slider'
import { InputWithSuffix } from '@/components/ui/input-group'
import { clampStartDistance } from '@/lib/shapes/text-on-path-session'

import { FOCUS_RING_CLASS } from '../constants/focus-ring-class'
import { SEGMENT_LAST_CLASS } from '../constants/segment-classes'

interface StartPositionControlProps {
  /** Owned by the toolbar root — see where it renders this control. */
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The draft's raw start distance; clamped to the path here. */
  startDistance: number
  pathLength: number
  onStartDistanceChange: (startDistance: number) => void
}

/**
 * AC-4: the start position can be changed. The spec's pill has no "Position" button — this
 * horizontal-arrows trigger is the slot it actually provides. Rendered as the last child of the
 * Offset/Position segmented group.
 */
export function StartPositionControl({
  open,
  onOpenChange,
  startDistance: rawStartDistance,
  pathLength,
  onStartDistanceChange,
}: StartPositionControlProps) {
  const startDistance = clampStartDistance(rawStartDistance, pathLength)
  const positionDisabled = pathLength <= 0

  const commitStartDistance = (raw: string) => {
    const parsed = Number.parseFloat(raw)

    if (!Number.isFinite(parsed)) return
    onStartDistanceChange(clampStartDistance(parsed, pathLength))
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          disabled={positionDisabled}
          aria-label="Start position"
          className={cn(
            'flex flex-shrink-0 items-center gap-2 border-none bg-transparent px-3 text-xs font-medium text-foreground transition-colors',
            FOCUS_RING_CLASS,
            SEGMENT_LAST_CLASS,
            positionDisabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer hover:bg-accent'
          )}
        >
          <MoveHorizontal className="h-4 w-4" />
          <ChevronDown className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        // Downward, matching the spec — and away from the text card directly above the
        // pill. Opening upward put this behind the card, and the toolbar root's
        // `zIndex: 9999` creates a stacking context the portalled popover loses to, so
        // only its right-hand sliver showed.
        side="bottom"
        align="end"
        sideOffset={8}
        // Explicitly above the toolbar root, so it can never be occluded by it again.
        className="z-[10000] w-[320px]"
        // Radix portals this to document.body, outside the toolbar root.
        data-text-edit-keepalive=""
      >
        <div className="flex flex-col gap-2">
          {/* Title case, per WMPCM-8692's design review of the popover cards. */}
          <span className="text-sm font-medium text-foreground">Start Position</span>
          <div className="flex items-center gap-3">
            <Slider
              min={0}
              max={Math.max(pathLength, 1)}
              step={1}
              value={[startDistance]}
              onValueChange={([next]) =>
                onStartDistanceChange(clampStartDistance(next ?? 0, pathLength))
              }
              aria-label="Start position along the path"
            />
            <InputWithSuffix
              size="compact"
              suffix="pt"
              type="number"
              value={Math.round(startDistance)}
              onChange={(e) => commitStartDistance(e.target.value)}
              aria-label="Start position value"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
